import { clearOrganizedCache } from "@/layouts/signed-in/views/Admin/SubmissionsTable/children/Search";
import { SubmissionDetailsHeaderProps } from "@/models/dynamic-submission/dynamic-submission-detail/DyanmicSubmissionDetail.model";
import { approveSubmission } from "@/services/submission-service";
import { EditOutlined, FilePdfOutlined, LeftOutlined } from "@ant-design/icons";
import { Button, Space, message } from "antd";
import { FC, useState } from "react";
import { MdDownload } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

interface LocationState {
	submissionsTableParams?: any;
	isAdminView?: boolean;
	formMode?: string;
}

export const SubmissionDetailsHeader: FC<SubmissionDetailsHeaderProps> = ({
	isQuickView,
	toggleSubmission,
	setModalIndex,
	responsesLength,
	dispatch,
	state,
	exportToCSV,
	downloadPdf,
	organization,
	onRefresh,
}) => {
	const { pdfLoading, data } = state;
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as LocationState;
	const [approving, setApproving] = useState(false);

	// Determine if we should show approve/edit buttons (org 28 specific)
	const showActionButtons = organization?.id === 28;
	const formMode =
		locationState?.submissionsTableParams?.selectedFormMode ||
		locationState?.formMode;
	const isPendingApproval = formMode === "pending_approval";
	const isApproved = formMode === "approved";
	const showApproveButton = showActionButtons && isPendingApproval;
	const showEditButton = showActionButtons && (isPendingApproval || isApproved);

	// Handle approve action
	const handleApprove = async () => {
		if (!data?.id) return;

		setApproving(true);
		try {
			await approveSubmission(data.id, { isRegularApproval: true });
			message.success("Submission approved successfully");

			// Clear the cache to ensure fresh data
			clearOrganizedCache();

			// Refresh if callback provided
			if (onRefresh) {
				onRefresh();
			}

			// Navigate back to submissions table with refresh query parameter
			const locationId = data?.location?.id;
			const isAdminView = locationState?.isAdminView;
			const submissionsTableParams = locationState?.submissionsTableParams;
			const refreshTimestamp = Date.now();

			if (isAdminView) {
				navigate(`/admin/submissions?refresh=${refreshTimestamp}`, {
					state: {
						submissionsTableParams,
						isAdminView: true,
					},
				});
			} else {
				navigate(
					`/locations/${locationId}/submissions?refresh=${refreshTimestamp}`,
					{
						state: {
							submissionsTableParams,
							locationId,
							isAdminView: false,
						},
					},
				);
			}
		} catch (error: any) {
			message.error(`Failed to approve submission: ${error.message}`);
		} finally {
			setApproving(false);
		}
	};

	// Handle edit action
	const handleEdit = async () => {
		if (!data) return;

		try {
			const isDraft = !data.end_time;
			const locationId = data?.location?.id;
			const isAdminView = locationState?.isAdminView;
			const submissionsTableParams = locationState?.submissionsTableParams;

			// For org 28 (and potentially other orgs), the organized endpoint excludes raw_input
			// for pending_approval and approved submissions to reduce payload size.
			// If raw_input is missing, fetch the full submission data before navigating
			let responseData = data.raw_input;
			if (
				!responseData ||
				(typeof responseData === "object" &&
					Object.keys(responseData).length === 0)
			) {
				// Import the submission service function
				const { getSQLSubmissionWithAsset } = await import(
					"@/services/submission-service"
				);

				// Fetch the full submission data including raw_input
				const fullSubmission = await getSQLSubmissionWithAsset(data.id, false);
				responseData = fullSubmission.raw_input;
			}

			// Navigate to the form with the submission data pre-loaded
			navigate(`/forms/${data.form_id}`, {
				state: {
					responseData: responseData,
					draftId: data.id,
					submissionId: data.id,
					formId: data.form_id,
					isSubmissionCompleted: Boolean(data.end_time),
					isEditingSubmission: true,
					locationId: data.location_id,
					savedLocation: {
						id: data.location_id,
						name: data.location?.name,
						address1: data.location?.address1,
						city: data.location?.city,
						state: data.location?.state,
						zip: data.location?.zip,
					},
					chosenAssetType: responseData?.assetType,
					isQA: false,
					fromSubmissionsTable: true,
					submissionsTableParams: {
						...submissionsTableParams,
						isAdminView,
						locationId,
					},
				},
			});
		} catch (error) {
			console.error("Failed to fetch submission data for editing:", error);
			// Show error message to user
			const { message } = await import("antd");
			message.error("Failed to load submission data. Please try again.");
		}
	};

	return (
		<div className="submission-header d-flex flex-row align-items-center justify-content-between">
			{/* Left side navigation (back arrow) */}
			{!isQuickView ? (
				toggleSubmission && (
					<Button
						type="link"
						onClick={() => {
							const locationId = state.data?.location?.id;
							const isAdminView = locationState?.isAdminView;
							const submissionsTableParams =
								locationState?.submissionsTableParams;

							if (isAdminView) {
								navigate("/admin/submissions", {
									state: {
										submissionsTableParams,
										isAdminView: true,
									},
								});
							} else {
								navigate(`/locations/${locationId}/submissions`, {
									state: {
										submissionsTableParams,
										locationId,
										isAdminView: false,
									},
								});
							}
						}}
						icon={<LeftOutlined />}
					>
						Back to Submissions
					</Button>
				)
			) : (
				/* If quickView, use the "previous" arrow button */
				<Button
					type="link"
					icon={<LeftOutlined />}
					onClick={() => {
						dispatch({ type: "SET_LOADING", payload: true });
						if (setModalIndex) {
							setModalIndex((prev) =>
								prev === 0 ? responsesLength - 1 : prev - 1,
							);
						}
						setTimeout(() => {
							dispatch({ type: "SET_LOADING", payload: false });
						}, 500);
					}}
				/>
			)}

			{/* Right side actions (if toggleSubmission is present, that means "full view", not quickView) */}
			{toggleSubmission && (
				<div className="d-flex gap-2">
					<Space size="small">
						{/* Approve and Edit buttons for org 28 */}
						{showApproveButton && (
							<Button
								type="primary"
								onClick={handleApprove}
								loading={approving}
							>
								Approve
							</Button>
						)}
						{showEditButton && (
							<Button icon={<EditOutlined />} onClick={handleEdit}>
								Edit
							</Button>
						)}

						{/* Export buttons */}
						<Button onClick={exportToCSV} icon={<MdDownload />}>
							CSV
						</Button>
						<Button
							onClick={downloadPdf}
							loading={pdfLoading}
							icon={<FilePdfOutlined />}
						>
							PDF
						</Button>
					</Space>
				</div>
			)}

			{/* QuickView "Next" arrow */}
			{isQuickView && (
				<Button
					type="link"
					icon={<LeftOutlined style={{ transform: "rotate(180deg)" }} />}
					onClick={() => {
						dispatch({ type: "SET_LOADING", payload: true });
						if (setModalIndex) {
							setModalIndex((prev) =>
								prev === responsesLength - 1 ? 0 : prev + 1,
							);
						}
						setTimeout(() => {
							dispatch({ type: "SET_LOADING", payload: false });
						}, 500);
					}}
				/>
			)}
		</div>
	);
};
